import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Toast } from '@/components/shopify-web-components'
import { CardImageUpload } from '@/components/image-uploader/CardImageUpload'
import { CompactImageUpload } from '@/components/image-uploader/CompactImageUpload'
import {
  buildGreetingCardPreview,
  getImageDetails,
  readFileAsDataUrl,
  validateImageUpload,
  type ImageUploaderFileType,
  type UploadCompletePayload,
  type UploadedImageDetails,
} from '@/components/image-uploader/imageUploadUtils'
import { shopifyAuthenticatedFetch } from '@/utils/shopifyAuthenticatedFetch'

type ImageUploaderProps = {
  label: string
  value?: File | null
  onChange: (fileUrl: string | null) => void
  currentImageUrl?: string
  initialImageUrl?: string
  maxSizeMB?: number
  maxSizeErrorMessage?: string
  accept?: string
  disabled?: boolean
  variant?: 'card' | 'compact'
  compactSize?: number | 'fill'
  compactEmptyContent?: React.ReactNode
  compactOverlayLabel?: string | null
  fileType: ImageUploaderFileType
  onImageDetails?: (details: UploadedImageDetails) => void
  validateImageDetails?: (details: UploadedImageDetails) => string | null
  onUploadComplete?: (payload: UploadCompletePayload) => void
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  label,
  onChange,
  currentImageUrl,
  initialImageUrl,
  maxSizeMB = 10,
  maxSizeErrorMessage,
  accept = 'image/*',
  disabled = false,
  variant = 'card',
  compactSize = 96,
  compactEmptyContent,
  compactOverlayLabel = 'Replace image',
  fileType,
  onImageDetails,
  validateImageDetails,
  onUploadComplete,
}) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [toastError, setToastError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset preview when store value changes (for discard functionality)
  useEffect(() => {
    if (currentImageUrl !== undefined) {
      // If currentImageUrl is empty and we have a preview, clear it
      if (!currentImageUrl && preview) {
        setPreview(null)
      }
      // If currentImageUrl has a value and it's different from our preview, clear preview
      // so the currentImageUrl (from store) takes precedence
      else if (currentImageUrl && currentImageUrl !== preview) {
        setPreview(null)
      }
    }
  }, [currentImageUrl, preview])

  const maxSizeBytes = maxSizeMB * 1024 * 1024

  const validateFile = useCallback(
    (file: File) =>
      validateImageUpload({
        file,
        fileType,
        maxSizeBytes,
        maxSizeMB,
        maxSizeErrorMessage,
      }),
    [fileType, maxSizeBytes, maxSizeErrorMessage, maxSizeMB],
  )

  const handleFileSelect = useCallback(
    async (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setToastError(validationError)
        return
      }

      setToastError(null)

      try {
        const [fileData, imageDetails, previewUpload] = await Promise.all([
          readFileAsDataUrl(file),
          getImageDetails(file),
          fileType === 'greeting_card_design'
            ? buildGreetingCardPreview(file).catch(() => null)
            : Promise.resolve(null),
        ])

        setPreview(fileData)
        onImageDetails?.(imageDetails)

        const imageDetailsError = validateImageDetails?.(imageDetails)
        if (imageDetailsError) {
          setToastError(imageDetailsError)
          return
        }

        setIsUploading(true)

        const uploadData = {
          fileType,
          fileData,
          fileName: file.name,
          mimeType: file.type,
          previewFileData: previewUpload?.fileData,
          previewFileName: previewUpload?.fileName,
          previewMimeType: previewUpload?.mimeType,
          imageWidth: imageDetails.width,
          imageHeight: imageDetails.height,
          shop: new URLSearchParams(
            globalThis.window?.location.search || '',
          ).get('shop'),
        }

        const response = await shopifyAuthenticatedFetch('/api/file/upload', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(uploadData),
        })

        const result = await response.json()

        if (result.success && result.fileUrl) {
          onChange(result.fileUrl)
          onUploadComplete?.(result)
        } else {
          setToastError(result.error || 'Failed to upload file')
          setPreview(null)
        }
      } catch {
        setToastError('Failed to upload file')
        setPreview(null)
      } finally {
        setIsUploading(false)
      }
    },
    [
      fileType,
      onChange,
      onImageDetails,
      onUploadComplete,
      validateFile,
      validateImageDetails,
    ],
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect],
  )

  const handleButtonClick = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }, [isUploading])

  // Priority: preview (new upload) > currentImageUrl (store value) > initialImageUrl (server value)
  const displayImageUrl = preview || currentImageUrl || initialImageUrl
  const hasImage = Boolean(displayImageUrl)
  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      onChange={handleFileInputChange}
      style={{ display: 'none' }}
      disabled={disabled || isUploading}
    />
  )

  return (
    <>
      {toastError && (
        <Toast
          content={toastError}
          error
          onDismiss={() => setToastError(null)}
        />
      )}

      {variant === 'compact'
        ? (
            <CompactImageUpload
              label={label}
              displayImageUrl={displayImageUrl}
              hasImage={hasImage}
              disabled={disabled}
              isUploading={isUploading}
              compactSize={compactSize}
              compactEmptyContent={compactEmptyContent}
              compactOverlayLabel={compactOverlayLabel}
              hiddenInput={hiddenInput}
              onButtonClick={handleButtonClick}
            />
          )
        : null}

      {variant === 'card'
        ? (
            <CardImageUpload
              label={label}
              displayImageUrl={displayImageUrl}
              hasImage={hasImage}
              disabled={disabled}
              isUploading={isUploading}
              hiddenInput={hiddenInput}
              onButtonClick={handleButtonClick}
            />
          )
        : null}
    </>
  )
}
